import { classDefinitionLabel, ClassKey } from '@const/class.constants';
import { Item } from '@dto/architecture.dto';
import { Empty, Tabs } from 'antd';
import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Label } from 'src/data/Label';
import { normalizeSearchQueryForApi } from '../domain/queryCodec';
import { useSearchStore } from '../store/useSearchStore';
import { GLOB } from 'src/util/Glob';
import '../styles/SearchResults.less';
import SearchResultsTable from './SearchResultsTable';
import useSearchResults from '../hooks/useSearchResults';
import { classDefinitionLabelSearch, ClassKeySearch } from '../constants';

interface Tab {
  label: string;
  classKey: string;
  items: Item[];
}

function useQuery(): [string, string, URLSearchParams] {
  const { pathname, search } = useLocation();
  const rawQuery = search.startsWith('?') ? search.slice(1) : search;

  return useMemo(
    () => [pathname, rawQuery, new URLSearchParams(search)],
    [pathname, rawQuery, search]
  );
}

const SearchResultsPage = () => {
  const isLabelDataLoading = useSearchStore(s => s.isLabelDataLoading);
  const loadLabelData = useSearchStore(s => s.loadLabelData);
  const [path, rawQuery, query] = useQuery();

  useEffect(() => {
    void loadLabelData();
  }, [loadLabelData]);

  const normalizedStr = useMemo(
    () => normalizeSearchQueryForApi(rawQuery),
    [rawQuery]
  );

  const searchTerm = query.get('search') || query.get('find') || '';
  const api = normalizedStr
    ? `/api/search/v1/item?${normalizedStr}`
    : `/api/search/v1/item/${encodeURIComponent(searchTerm)}`;

  const parmForTitle = rawQuery || searchTerm;
  const [fetchedItems, fetchedLoading] = useSearchResults(path, parmForTitle, api);

  const isDataLoading = isLabelDataLoading || fetchedLoading;

  useEffect(() => {
    document.title = (GLOB.instanceLabel || Label.XORMON_NG) + ' | Search';
  }, [searchTerm, normalizedStr]);

  const tabs: Tab[] = useMemo(() => {
    if (!fetchedItems.length) return [];

    const initialTabs: Tab[] = [
      { label: classDefinitionLabelSearch[ClassKeySearch.SERVER], classKey: ClassKey.VIRTUALIZATION, items: [] },
      { label: classDefinitionLabel[ClassKey.STORAGE], classKey: ClassKey.STORAGE, items: [] },
      { label: classDefinitionLabel[ClassKey.SAN], classKey: ClassKey.SAN, items: [] },
      { label: classDefinitionLabel[ClassKey.LAN], classKey: ClassKey.LAN, items: [] },
    ];

    const grouped = fetchedItems.reduce<Tab[]>((acc, item) => {
      const existingIndex = acc.findIndex((tab) => tab.classKey === item.class);
      if (existingIndex >= 0) {
        return acc.map((tab, i) =>
          i === existingIndex ? { ...tab, items: [...tab.items, item] } : tab
        );
      }
      const label =
        classDefinitionLabelSearch[item.class as ClassKeySearch] ||
        item.class.charAt(0).toUpperCase() + item.class.slice(1);
      return [...acc, { label, classKey: item.class, items: [item] }];
    }, initialTabs);

    return grouped.map((tab) => ({
      ...tab,
      items: [...tab.items].sort((a, b) => GLOB.naturalSort(a.label, b.label)),
    }));
  }, [fetchedItems]);

  if (!fetchedItems.length) {
    return isDataLoading
      ? <SearchResultsTable items={[]} tabSelected='' loading={true} />
      : <div className='xm-search-fail'><Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description='No Data' /></div>;
  }

  return (
    <Tabs
      className='xm-search-tabs'
      items={tabs
        .filter((tab) => tab.items.length > 0)
        .map((tab) => ({
          key: tab.label,
          label: tab.label,
          className: 'xm-search-tab-wrapper',
          children: (
            <SearchResultsTable
              key={tab.classKey}
              items={tab.items}
              tabSelected={tab.label}
              loading={fetchedLoading}
            />
          ),
        }))}
    />
  );
};

export default SearchResultsPage;
